import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BottomNavigation,
  BottomNavigationAction,
  GlobalStyles,
  Paper,
} from '@mui/material';
import FlightTakeoffOutlinedIcon from '@mui/icons-material/FlightTakeoffOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import { DOCUMENT } from '../constants/documentTheme';
import { getPackagesListingPath } from '../utils/partnerCatalogStorage';
import {
  getTravelerBottomNavKey,
  TRAVELER_BOTTOM_NAV_HEIGHT,
  travelerPublicBottomTabs,
  travelerSignedInBottomTabs,
} from '../utils/travelerMobileNav';
import { NavMessageBadge } from './NavMessageBadge';
import useInquiryUnreadCount from '../hooks/useInquiryUnreadCount';

const ICONS = {
  trips: FlightTakeoffOutlinedIcon,
  memories: PhotoLibraryOutlinedIcon,
  packages: Inventory2OutlinedIcon,
  inquiries: ForumOutlinedIcon,
  destinations: FmdGoodOutlinedIcon,
  bookings: ConfirmationNumberOutlinedIcon,
  plan: MapOutlinedIcon,
  guides: MenuBookOutlinedIcon,
};

/**
 * Primary traveler destinations on mobile — mirrors partner bottom nav pattern.
 * Top chrome tabs stay on md+; secondary links remain in the hamburger drawer.
 */
const TravelerMobileBottomNav = ({ signedIn = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const packagesPath = getPackagesListingPath();
  const tabs = signedIn
    ? travelerSignedInBottomTabs(packagesPath)
    : travelerPublicBottomTabs(packagesPath);
  const activeKey = getTravelerBottomNavKey(location.pathname, { signedIn });
  const inquiryUnread = useInquiryUnreadCount({ enabled: signedIn, viewer: 'traveler' });

  useEffect(() => {
    document.body.classList.add('has-traveler-bottom-nav');
    return () => document.body.classList.remove('has-traveler-bottom-nav');
  }, []);

  return (
    <>
      <GlobalStyles
        styles={{
          'body.has-traveler-bottom-nav': {
            paddingBottom: `calc(${TRAVELER_BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
          },
          '@media (min-width: 900px)': {
            'body.has-traveler-bottom-nav': {
              paddingBottom: 0,
            },
          },
        }}
      />
      <Paper
        component="nav"
        elevation={0}
        data-testid="traveler-mobile-bottom-nav"
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
          zIndex: 30,
          borderRadius: 0,
          borderTop: `1px solid ${DOCUMENT.navRule}`,
          bgcolor: DOCUMENT.nav,
          pb: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <BottomNavigation
          showLabels
          value={tabs.some((tab) => tab.key === activeKey) ? activeKey : false}
          onChange={(_event, value) => {
            const tab = tabs.find((item) => item.key === value);
            if (tab) navigate(tab.to);
          }}
          sx={{
            height: TRAVELER_BOTTOM_NAV_HEIGHT,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden',
            bgcolor: 'transparent',
            '& .MuiBottomNavigationAction-root': {
              color: 'rgba(246,239,228,0.72)',
              flex: '1 1 0',
              minWidth: 0,
              maxWidth: 'none',
              px: 0.25,
              py: 0.75,
              overflow: 'hidden',
              fontFamily: DOCUMENT.sans,
              '&.Mui-selected': { color: DOCUMENT.navAccent },
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.58rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              mt: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              '&.Mui-selected': { fontSize: '0.58rem' },
            },
          }}
        >
          {tabs.map((tab) => {
            const Icon = ICONS[tab.key];
            const icon = <Icon sx={{ fontSize: 22 }} />;
            return (
              <BottomNavigationAction
                key={tab.key}
                value={tab.key}
                label={t(tab.labelKey)}
                icon={tab.key === 'inquiries'
                  ? <NavMessageBadge count={inquiryUnread}>{icon}</NavMessageBadge>
                  : icon}
                data-testid={`traveler-bottom-nav-${tab.key}`}
              />
            );
          })}
        </BottomNavigation>
      </Paper>
    </>
  );
};

export default TravelerMobileBottomNav;
